/**
 * OrderDetailDialog — dettaglio ordine + azioni workflow.
 * Approva/Rifiuta (orders.approve), Segna consegnato con conferma quantità
 * (orders.deliver → DeliveryConfirmDialog: totale o parziale),
 * Modifica (In attesa: richiedente+chi approva; Approvato: solo chi approva),
 * Annulla (solo richiedente, solo pending).
 */
import React, { useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePermissions } from '../lib/usePermissions';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Check, Clock, FileDown, Loader2, Mail, PackageCheck, Pencil, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { ORDER_STATUS_META, OVERDUE_DAYS, fmtPrice, overdueDays } from '../pages/OrdersPage';
import EmailOrdersDialog from './EmailOrdersDialog';
import NewOrderDialog from './NewOrderDialog';
import DeliveryConfirmDialog from './DeliveryConfirmDialog';

function fmtDateTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function OrderDetailDialog({ order, onClose, onChanged }) {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);
  const [busyReorder, setBusyReorder] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelivery, setShowDelivery] = useState(false);

  const reorder = async () => {
    setBusyReorder(true);
    try {
      const r = await api.post(`/orders/${order.order_id}/reorder`);
      const dropped = r.data.dropped_items || [];
      toast.success(`Riordino inviato — ${r.data.number}${dropped.length ? ` (${dropped.length} articoli non più a catalogo esclusi)` : ''}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Riordino fallito');
    } finally {
      setBusyReorder(false);
    }
  };

  if (!order) return null;
  const meta = ORDER_STATUS_META[order.status] || ORDER_STATUS_META.pending;
  const isOwner = user?.user_id === order.requested_by;
  const canEdit = (order.status === 'pending' && (has('orders.approve') || (isOwner && has('orders.create'))))
    || (order.status === 'approved' && has('orders.approve'));
  const waiting = overdueDays(order);

  const downloadPdf = async () => {
    setBusyPdf(true);
    try {
      const r = await api.post('/orders/summary-pdf', { order_ids: [order.order_id] }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${order.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download PDF fallito');
    } finally {
      setBusyPdf(false);
    }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operazione fallita');
    } finally {
      setBusy(false);
      setShowReject(false);
      setRejectNote('');
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono">{order.number}</span>
            <span className={`px-2 py-0.5 text-[11px] font-semibold border ${meta.cls}`} data-testid="order-detail-status">{meta.label}</span>
          </DialogTitle>
          <DialogDescription>
            {order.store_name} — richiesto da {order.requested_by_name} il {fmtDateTime(order.created_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-slate-200 divide-y divide-slate-100" data-testid="order-items-list">
          {order.items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 truncate">{it.name}</p>
                <p className="text-[11px] text-slate-400 font-mono">{it.code || it.category}</p>
              </div>
              <span className="text-xs text-slate-500 shrink-0">
                x{it.qty}
                {(it.qty_delivered || 0) > 0 && (it.qty_delivered || 0) < it.qty && (
                  <span className="text-cyan-600 font-semibold"> ({it.qty_delivered} consegnati)</span>
                )}
              </span>
              <span className="text-sm text-slate-600 w-20 text-right shrink-0">{fmtPrice(it.price)}</span>
              <span className="text-sm font-semibold text-slate-900 w-24 text-right shrink-0">{fmtPrice(it.subtotal)}</span>
            </div>
          ))}
          <div className="flex justify-between px-3 py-2 bg-slate-50">
            <span className="text-sm font-semibold text-slate-700">Totale ({order.items_count} articoli)</span>
            <span className="text-sm font-bold text-slate-900" data-testid="order-detail-total">
              {fmtPrice(order.total)}
              {order.unpriced_lines > 0 && <span className="text-xs text-amber-600 font-normal ml-1">(+{order.unpriced_lines} senza prezzo)</span>}
            </span>
          </div>
        </div>

        {order.notes && (
          <div className="text-sm bg-slate-50 border border-slate-200 p-3">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Note</p>
            <p className="text-slate-700 whitespace-pre-wrap">{order.notes}</p>
          </div>
        )}

        {order.decided_at && (
          <p className="text-xs text-slate-500" data-testid="order-decision-info">
            {order.status === 'rejected' ? 'Rifiutato' : 'Approvato'} da {order.decided_by_name} il {fmtDateTime(order.decided_at)}
            {order.decision_note && <span className="block mt-0.5 text-red-600">Motivo: {order.decision_note}</span>}
          </p>
        )}
        {waiting > 0 && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 p-2 flex gap-1.5" data-testid="order-overdue-info">
            <Clock className="w-4 h-4 shrink-0" />
            <span>Approvato {waiting} giorni fa (oltre {OVERDUE_DAYS}) e non ancora arrivato: sollecita il fornitore o registra la consegna se è già in negozio.</span>
          </p>
        )}
        {order.delivered_at && (
          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 p-2" data-testid="order-delivered-info">
            Consegnato il {fmtDateTime(order.delivered_at)}
            {order.inventory_created > 0 && ` — ${order.inventory_created} strumenti aggiunti al Magazzino`}
            {(order.inventory_skipped || []).length > 0 && ` (${order.inventory_skipped.length} articoli non a Magazzino: nessuna categoria inventario mappata)`}
          </p>
        )}

        {showReject && (
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Motivo del rifiuto</label>
            <Textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)} rows={2} maxLength={500}
                      className="rounded-none resize-none" data-testid="order-reject-note" autoFocus />
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {(has('orders.approve') || has('orders.create')) && (
            <div className="flex gap-2 mr-auto">
              {has('orders.approve') && (
                <>
                  <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => setShowEmail(true)} data-testid="order-email-btn">
                    <Mail className="w-3.5 h-3.5" /> Invia mail
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={downloadPdf} disabled={busyPdf} data-testid="order-pdf-btn">
                    {busyPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                  </Button>
                </>
              )}
              {has('orders.create') && (
                <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={reorder} disabled={busyReorder}
                        title="Duplica questo ordine in un nuovo ordine in attesa" data-testid="order-reorder-btn">
                  {busyReorder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Riordina
                </Button>
              )}
              {canEdit && (
                <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => setShowEdit(true)} data-testid="order-edit-btn">
                  <Pencil className="w-3.5 h-3.5" /> Modifica
                </Button>
              )}
            </div>
          )}
          {order.status === 'pending' && isOwner && (
            <Button variant="outline" disabled={busy} className="gap-1.5 text-slate-600"
                    onClick={() => act(() => api.post(`/orders/${order.order_id}/cancel`), 'Ordine annullato')}
                    data-testid="order-cancel-btn">
              <X className="w-4 h-4" /> Annulla ordine
            </Button>
          )}
          {order.status === 'pending' && has('orders.approve') && (
            showReject ? (
              <>
                <Button variant="outline" disabled={busy} onClick={() => setShowReject(false)}>Indietro</Button>
                <Button variant="destructive" disabled={busy} className="gap-1.5"
                        onClick={() => act(() => api.post(`/orders/${order.order_id}/reject`, { note: rejectNote }), 'Ordine rifiutato')}
                        data-testid="order-reject-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Conferma rifiuto
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" disabled={busy} className="gap-1.5 text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => setShowReject(true)} data-testid="order-reject-btn">
                  <X className="w-4 h-4" /> Rifiuta
                </Button>
                <Button disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => act(() => api.post(`/orders/${order.order_id}/approve`), 'Ordine approvato')}
                        data-testid="order-approve-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approva
                </Button>
              </>
            )
          )}
          {(order.status === 'approved' || order.status === 'partially_delivered') && has('orders.deliver') && (
            <Button disabled={busy} className="gap-1.5 bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowDelivery(true)} data-testid="order-deliver-btn">
              <PackageCheck className="w-4 h-4" />
              {order.status === 'partially_delivered' ? 'Completa consegna' : 'Segna consegnato'}
            </Button>
          )}
        </DialogFooter>
        <EmailOrdersDialog
          open={showEmail}
          onClose={() => setShowEmail(false)}
          orderIds={[order.order_id]}
          onSent={() => setShowEmail(false)}
        />
        <NewOrderDialog
          open={showEdit}
          editOrder={order}
          stores={[]}
          onClose={() => setShowEdit(false)}
          onCreated={() => { setShowEdit(false); onChanged(); }}
        />
        <DeliveryConfirmDialog
          order={showDelivery ? order : null}
          onClose={() => setShowDelivery(false)}
          onDelivered={() => { setShowDelivery(false); onChanged(); }}
        />
      </DialogContent>
    </Dialog>
  );
}
